import { CheckCircle, CircleAlert, CircleX } from "lucide-react";
import { useCallback, useEffect, useRef } from "react";
import { createPortal } from "react-dom";

interface AlertModalProps {
    message: string;
    type: "success" | "error" | "warning";
    onClose: () => void;
}

function AlertModal({ message, type, onClose }: AlertModalProps) {
    const overlayRef = useRef<HTMLDivElement | null>(null);

    const dismissModal = useCallback(() => {
        onClose();
    }, [onClose]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                overlayRef.current &&
                !overlayRef.current.contains(event.target as Node)
            ) {
                dismissModal();
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [dismissModal]);

    const getTypeStyles = () => {
        switch (type) {
            case "success":
                return {
                    icon: (
                        <CheckCircle className="size-[4rem] text-green-500" />
                    ),
                    borderColor: "border-green-500",
                };
            case "error":
                return {
                    icon: <CircleX className="size-[4rem] text-danger" />,
                    borderColor: "border-danger",
                };
            case "warning":
                return {
                    icon: <CircleAlert className="size-[4rem] text-warning" />,
                    borderColor: "border-primary",
                };
            default:
                return {
                    icon: null,
                    borderColor: "border-border-color",
                };
        }
    };

    const { icon } = getTypeStyles();

    return createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
            <div
                className={`z-[500] flex max-h-full w-full max-w-md flex-col items-center gap-1 overflow-y-auto rounded-2xl border-2 border-border-color bg-background-color px-4 py-8 text-text-color shadow-lg sm:w-[400px] md:px-12`}
                ref={overlayRef}
            >
                {icon}
                <p className="my-4 text-center">{message}</p>
                <button
                    className="btn btn-pill-primary mt-4 w-[100px]"
                    onClick={dismissModal}
                    autoFocus
                >
                    Ok
                </button>
            </div>
        </div>,
        document.body,
    );
}

export default AlertModal;
